import React from 'react';
import { MapPin, Lock, Check } from 'lucide-react';

// Native-only priming screen shown once before the first session start.
// iOS asks for location in two steps — "While Using" first, then a later
// "Keep Always?" escalation — and users who don't pick Always get frozen
// tracking the moment their screen locks. Android is one step: "While using
// the app" is enough, because tracking rides a location foreground service
// started from GO LIVE (that's also why the Android build carries no
// ACCESS_BACKGROUND_LOCATION permission). Same screen, per-platform steps.
const LocationPrimer = ({ onContinue }) => {
  const isAndroid = window.Capacitor?.getPlatform?.() === 'android';
  return (
    <div className="absolute inset-0 z-[4000] bg-black/95 flex items-center justify-center p-6 animate-in fade-in duration-300">
      <div className="w-full max-w-sm bg-zinc-900 border border-white/10 rounded-[2rem] p-6 shadow-2xl flex flex-col relative overflow-hidden">

        {/* Glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-40 h-40 bg-brand-teal/20 rounded-full blur-3xl -mt-20 pointer-events-none"></div>

        <div className="relative z-10 flex flex-col items-center text-center">
          <div className="w-16 h-16 rounded-2xl bg-brand-teal/15 border border-brand-teal/40 flex items-center justify-center mb-4">
            <MapPin className="text-brand-teal" size={30} />
          </div>

          <h2 className="text-xl font-black italic text-white uppercase tracking-tighter mb-2">
            Keep Tracking With The Screen Off
          </h2>
          <p className="text-zinc-400 text-sm font-medium mb-5 leading-snug">
            {isAndroid ? (
              <>To record your full route and keep your squad seeing you live — even in
              your pocket — Bender Quest tracks your location while a session is running,
              including with the screen off. A persistent notification shows the whole
              time, and tracking stops when you end the session.</>
            ) : (
              <>To record your full route and keep your squad seeing you live — even in
              your pocket — Bender Quest needs <span className="text-white font-bold">Always</span> location access.</>
            )}
          </p>

          {/* Permission steps — iOS is a two-step escalation, Android is one tap */}
          <div className="w-full space-y-2.5 mb-6 text-left">
            <div className="flex items-start gap-3 bg-zinc-800/60 border border-zinc-700 rounded-xl p-3">
              <div className="w-6 h-6 rounded-full bg-zinc-700 text-white text-xs font-black flex items-center justify-center shrink-0 mt-0.5">1</div>
              <p className="text-xs font-bold text-zinc-300 leading-snug">
                {isAndroid
                  ? <>When Android asks, tap <span className="text-white">“While using the app.”</span></>
                  : <>First prompt: tap <span className="text-white">“Allow While Using App.”</span></>}
              </p>
            </div>
            <div className="flex items-start gap-3 bg-zinc-800/60 border border-brand-teal/40 rounded-xl p-3">
              <div className="w-6 h-6 rounded-full bg-brand-teal text-black text-xs font-black flex items-center justify-center shrink-0 mt-0.5">2</div>
              <p className="text-xs font-bold text-zinc-300 leading-snug">
                {isAndroid
                  ? <>That's it — the <span className="text-brand-teal">“Tracking your night out”</span> notification keeps your route alive while locked.</>
                  : <>A bit later iOS asks again — tap <span className="text-brand-teal">“Change to Always Allow.”</span></>}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 text-[10px] font-bold text-zinc-500 uppercase tracking-widest mb-5">
            <Lock size={12} /> You can change this anytime in Settings
          </div>

          <button
            onClick={onContinue}
            className="w-full bg-brand-teal hover:bg-teal-300 text-black font-black text-base py-4 rounded-xl uppercase tracking-widest shadow-lg active:scale-95 transition-all flex items-center justify-center gap-2"
          >
            <Check size={20} /> Got It — Let's Go
          </button>
        </div>
      </div>
    </div>
  );
};

export default LocationPrimer;
